import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import LanguageSwitcher from "./LanguageSwitcher";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const navigation = [
    { name: t("nav.services"), href: "/services" },
    { name: t("nav.caseStudies"), href: "/case-studies" },
    { name: t("nav.about"), href: "/about" },
    { name: t("nav.blog"), href: "/blog" },
    { name: t("nav.contact"), href: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-black/95 backdrop-blur-sm border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div
          className={`flex items-center justify-between h-20 ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/logo.png"
              alt="Solid Consulting"
              className="h-12 w-auto"
              width="132"
              height="48"
              loading="eager"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav
            className={`hidden lg:flex items-center gap-8 ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-base font-normal transition-colors duration-300 ${
                  location.pathname === item.href
                    ? "text-[#6FD2C0]"
                    : "text-white/70 hover:text-white"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA & Language Switcher */}
          <div
            className={`hidden lg:flex items-center gap-4 ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            <LanguageSwitcher />
            <Link to="/contact">
              <Button
                className={`bg-[#00FFD1] text-black hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] px-6 py-3 text-base font-medium transition-all duration-400 flex items-center gap-2 ${
                  isRTL ? "flex-row-reverse" : ""
                }`}
              >
                {t("nav.bookCall")}
                <ArrowRight
                  className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`}
                />
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button & Language */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              className="text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-black border-t border-white/10 animate-fade-in">
          <div className="px-6 py-6 space-y-4">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`block text-lg font-normal py-2 transition-colors duration-300 ${
                  location.pathname === item.href
                    ? "text-[#6FD2C0]"
                    : "text-white/70 hover:text-white"
                } ${isRTL ? "text-right" : "text-left"}`}
              >
                {item.name}
              </Link>
            ))}
            <div className="pt-4 border-t border-white/10">
              <Link to="/contact">
                <Button
                  className={`w-full bg-[#00FFD1] text-black hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] px-6 py-3 text-base font-medium flex items-center justify-center gap-2 ${
                    isRTL ? "flex-row-reverse" : ""
                  }`}
                >
                  {t("nav.bookCall")}
                  <ArrowRight
                    className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`}
                  />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
